import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Heart, User, Globe, Shield, Award, Phone, Mail, ArrowRight, Star, Users, Clock, Ear, ChevronLeft, ChevronRight } from 'lucide-react';
import Background from '../pic1.jpeg';
import Footer from './Footer';
import Header from './Header';

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // SEO: Add structured data for better search understanding
  useEffect(() => {
    // Add structured data for organization
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Listening Heart Support Platform",
      "description": "Free anonymous emotional support and crisis chat platform for people going through tough times.",
      "url": window.location.origin,
      "sameAs": [
        // Add your social media URLs here when available
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "crisis support",
        "availableLanguage": "English",
      },
      "areaServed": "Worldwide",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Emotional Support Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Anonymous Crisis Chat Support",
              "description": "Free confidential emotional support chat for people in crisis"
            },
            "price": "0",
            "priceCurrency": "USD"
          }
        ]
      }
    };

    // Add service schema
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Free Anonymous Emotional Support Chat",
      "description": "Free confidential emotional support chat service for people experiencing depression, anxiety, crisis, or anyone who needs someone to talk to during tough times",
      "provider": {
        "@type": "Organization",
        "name": "Listening Heart Support Platform"
      },
      "areaServed": "Worldwide",
      "audience": {
        "@type": "Audience",
        "audienceType": "People experiencing emotional distress, depression, anxiety, crisis, loneliness, or tough times"
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceType": "Online Chat Support",
        "availableLanguage": "English"
      },
      "isAccessibleForFree": true
    };

    // Add FAQ schema for common search queries
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is the emotional support chat really free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, our emotional support chat service is completely free. We accept donations to keep the service running, but there is no cost to access support."
          }
        },
        {
          "@type": "Question",
          "name": "Is the chat anonymous and confidential?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. All conversations are completely anonymous and confidential. No personal information is required to access our support."
          }
        },
        {
          "@type": "Question",
          "name": "Who can I talk to when I'm going through a tough time?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our platform connects you with trained peer supporters and compassionate listeners who understand what you're going through and provide judgment-free emotional support."
          }
        },
        {
          "@type": "Question",
          "name": "What if I'm having a mental health crisis?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our platform provides immediate emotional support. If you're in immediate danger, please contact emergency services. Our supporters can provide emotional support and help you through difficult moments."
          }
        }
      ]
    };

    // Create and append script tags for structured data
    const schemas = [organizationSchema, serviceSchema, faqSchema];
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.id = `schema-${index}`;
      document.head.appendChild(script);
    });

    // SEO Meta tags
    document.title = "Free Anonymous Emotional Support Chat | Talk to Someone During Tough Times | Listening Heart";
    
    const metaTags = [
      { name: 'description', content: 'Free anonymous emotional support chat for people going through depression, anxiety, crisis, or tough times. Talk to compassionate listeners who care.' },
      { name: 'keywords', content: 'free emotional support chat, anonymous depression support, anxiety help online, crisis chat support, someone to talk to, mental health support, tough times help, free therapy chat, emotional wellness, peer support' },
      { name: 'robots', content: 'index, follow' },
      { name: 'author', content: 'Listening Heart Support Platform' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      
      // Open Graph tags for social media
      { property: 'og:title', content: 'Free Anonymous Emotional Support Chat - Listening Heart' },
      { property: 'og:description', content: 'Free anonymous emotional support chat. Talk to someone during tough times. Depression, anxiety, crisis support available.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href },
      { property: 'og:site_name', content: 'Listening Heart Support Platform' },
      
      // Twitter Card tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Free Anonymous Emotional Support Chat - Listening Heart' },
      { name: 'twitter:description', content: 'Free anonymous emotional support chat. Talk to someone during tough times.' },
      
      // Additional SEO tags
      { name: 'theme-color', content: '#8b7355' },
      { name: 'msapplication-TileColor', content: '#8b7355' },
      { name: 'application-name', content: 'Listening Heart Support' },
      { name: 'apple-mobile-web-app-title', content: 'Listening Heart Support' },
      { name: 'format-detection', content: 'telephone=no' }
    ];

    metaTags.forEach(tag => {
      const existingTag = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (existingTag) {
        existingTag.setAttribute('content', tag.content);
      } else {
        const meta = document.createElement('meta');
        if (tag.name) meta.setAttribute('name', tag.name);
        if (tag.property) meta.setAttribute('property', tag.property);
        meta.setAttribute('content', tag.content);
        document.head.appendChild(meta);
      }
    });

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.href;

    // Cleanup function
    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-${index}`);
        if (script) script.remove();
      });
    };
  }, []);
  
  const encouragingMessages = [
    {
      title: "You Are Stronger Than You Know",
      message: "Every challenge you've faced has prepared you for this moment. You have overcome difficulties before, and you will overcome this too.",
      color: "from-stone-500 to-stone-600"
    },
    {
      title: "Your Story Matters",
      message: "Your experiences, struggles, and triumphs are unique and valuable. You matter, and your life has meaning and purpose.",
      color: "from-stone-400 to-neutral-600"
    },
    {
      title: "It's Okay to Not Be Okay",
      message: "Difficult emotions are part of the human experience. You don't have to pretend to be strong all the time. It's okay to ask for help.",
      color: "from-stone-600 to-stone-700"
    },
    {
      title: "Tomorrow Is a New Day",
      message: "No matter how dark today feels, tomorrow brings new possibilities. Healing happens one day at a time, one breath at a time.",
      color: "from-stone-600 to-stone-500"
    },
    {
      title: "You Are Not Alone",
      message: "Millions of people around the world are going through similar struggles. You are part of a community that understands and cares.",
      color: "from-stone-500 to-stone-600"
    },
    {
      title: "Small Steps Count",
      message: "Progress isn't always dramatic. Sometimes it's getting out of bed, taking a shower, or reaching out for help. Every small step matters.",
      color: "from-stone-600 to-stone-700"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % encouragingMessages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + encouragingMessages.length) % encouragingMessages.length);
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#faf9f7' }}>
      {/* SEO: Add semantic HTML structure */}
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />
     
      <main className="flex-grow" role="main">
        {activeTab === 'home' && (
          <>
            {/* Hero Section with SEO-optimized content */}
            <section className="relative overflow-hidden" aria-label="Free anonymous emotional support chat platform">
              <div
                className="relative text-white py-20 md:py-32 bg-cover bg-center transform transition-all duration-1000 ease-out"
                style={{
                  backgroundImage: `linear-gradient(135deg, rgba(139, 115, 85, 0.8), rgba(161, 136, 127, 0.6)), url(${Background})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-amber-900 opacity-45"></div>
                
                <div className="container mx-auto px-6 sm:px-8 flex flex-col lg:flex-row items-center relative z-10 gap-12">
                  <div className="lg:w-1/2 mb-12 lg:mb-0 transform transition-all duration-1000 ease-out">
                    <div className="space-y-6">
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        <span className="block text-amber-100 opacity-95">Free Anonymous Chat</span>
                        <span className="block text-stone-100 font-normal">For Emotional Support</span>
                        <span className="block text-sm sm:text-base font-normal tracking-widest text-stone-300 mt-4 uppercase">Talk to Someone • Crisis Support • Depression Help</span>
                      </h1>
                      <div className="w-24 h-0.5 bg-gradient-to-r from-amber-300 to-stone-400 transform transition-all duration-700"></div>
                      <p className="text-lg sm:text-xl leading-relaxed max-w-lg text-stone-100 opacity-90 font-light tracking-wide">
                        Going through a tough time? Need someone to talk to about depression, anxiety, or emotional distress? Our free, anonymous platform connects you with compassionate listeners.
                      </p>
                    </div>
                    <div className="mt-10">
                      <Link
                        to="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3"
                        className="group relative inline-flex items-center px-8 py-4 text-base font-medium tracking-wider uppercase transition-all duration-500 ease-out transform hover:scale-105"
                        style={{ 
                          backgroundColor: '#d4b5a0',
                          color: '#5d4e37',
                          borderRadius: '2px'
                        }}
                        aria-label="Start free anonymous emotional support chat"
                      >
                        <span className="relative z-10">Talk to Someone Now - Free</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-stone-300 to-amber-200 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-sm"></div>
                        <MessageCircle size={16} className="ml-3 transform group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </div>
                  </div>
                  
                  <div className="lg:w-1/2 flex justify-center lg:justify-end w-full">
                    <div className="relative w-full max-w-lg transform transition-all duration-1000 ease-out hover:scale-105">
                      <div className="absolute inset-0 bg-gradient-to-br from-stone-200 to-amber-100 rounded-lg blur-xl opacity-30"></div>
                      <div className="relative bg-stone-300/95 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-2xl border border-stone-200/50">
                        <h2 className="text-stone-800 font-light text-2xl mb-6 tracking-wide text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                          You Are Not Alone
                        </h2>
                        <div className="w-16 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-8"></div>
                        
                        <div className="relative h-64 overflow-hidden rounded-lg">
                          <div 
                            className="flex transition-transform duration-500 ease-in-out h-full"
                            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                          >
                            {encouragingMessages.map((slide, index) => (
                              <div
                                key={index}
                                className={`min-w-full h-full bg-gradient-to-br ${slide.color} p-6 flex flex-col justify-center text-white relative overflow-hidden`}
                              >
                                <div className="absolute inset-0 bg-black/10"></div>
                                <div className="relative z-10">
                                  <h3 className="text-xl font-medium mb-4 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                                    {slide.title}
                                  </h3>
                                  <p className="text-sm leading-relaxed text-center opacity-95 font-light">
                                    {slide.message}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <button
                            onClick={prevSlide}
                            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-stone/20 hover:bg-white/30 rounded-full p-2 transition-all duration-300"
                            aria-label="Previous encouraging message"
                          >
                            <ChevronLeft size={16} className="text-white" />
                          </button>
                          
                          <button
                            onClick={nextSlide}
                            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-stone/20 hover:bg-white/30 rounded-full p-2 transition-all duration-300"
                            aria-label="Next encouraging message"
                          >
                            <ChevronRight size={16} className="text-white" />
                          </button>
                          
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {encouragingMessages.map((_, index) => (
                              <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                  index === currentSlide ? 'bg-white' : 'bg-white/50'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                        
                        <div className="mt-6 text-center">
                          <Link
                            to="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3"
                            className="inline-flex items-center px-6 py-3 text-sm font-medium tracking-wider uppercase transition-all duration-500 ease-out transform hover:scale-105 text-white rounded-sm"
                            style={{ backgroundColor: '#8b7355' }}
                            aria-label="Get immediate emotional support through our chat"
                          >
                            Get Support Now
                            <Heart size={14} className="ml-2" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Services Section with SEO keywords */}
            <section className="py-20 sm:py-24" style={{ backgroundColor: '#f7f5f3' }} aria-label="Our emotional support services">
              <div className="container mx-auto px-6 sm:px-8">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 className="text-3xl sm:text-4xl font-light mb-6 text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Free Emotional Support Services
                  </h2>
                  <div className="w-24 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-8"></div>
                  <p className="text-stone-600 max-w-2xl mx-auto text-lg leading-relaxed font-light">
                    Our comprehensive support services help people going through depression, anxiety, crisis situations, and anyone who needs someone to talk to during tough times.
                  </p>
                </div>
               
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
                  <article className="group relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-white to-stone-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                    <div className="relative bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-lg border border-stone-200/50 transition-all duration-500 transform group-hover:-translate-y-2">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto transition-all duration-300 group-hover:scale-110" style={{ backgroundColor: '#e6d7c9' }}>
                        <Shield size={28} className="text-stone-700" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-light mb-4 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        Crisis Support Chat
                      </h3>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-6"></div>
                      <p className="text-stone-600 leading-relaxed text-center font-light">
                        Immediate emotional support for those experiencing crisis situations. Talk to someone who understands what you're going through without judgment.
                      </p>
                    </div>
                  </article>
                 
                  <article className="group relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-white to-stone-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                    <div className="relative bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-lg border border-stone-200/50 transition-all duration-500 transform group-hover:-translate-y-2">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto transition-all duration-300 group-hover:scale-110" style={{ backgroundColor: '#e6d7c9' }}>
                        <Award size={28} className="text-stone-700" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-light mb-4 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        Depression & Anxiety Support
                      </h3>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-6"></div>
                      <p className="text-stone-600 leading-relaxed text-center font-light">
                        Ongoing support for managing depression, anxiety, stress, and other mental health challenges. Connect with compassionate listeners who provide emotional wellness support.
                      </p>
                    </div>
                  </article>
                 
                  <article className="group relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-white to-stone-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                    <div className="relative bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-lg border border-stone-200/50 transition-all duration-500 transform group-hover:-translate-y-2">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto transition-all duration-300 group-hover:scale-110" style={{ backgroundColor: '#e6d7c9' }}>
                        <MessageCircle size={28} className="text-stone-700" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-light mb-4 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        Someone to Talk To
                      </h3>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-6"></div>
                      <p className="text-stone-600 leading-relaxed text-center font-light">
                        Sometimes you just need someone to listen. Our empathetic conversation partners provide a safe space where you feel truly heard, understood, and supported during difficult times.
                      </p>
                    </div>
                  </article>
                </div>
              </div>
            </section>
          </>
        )}
       
        {activeTab === 'how' && (
          <section className="py-20 sm:py-24" style={{ backgroundColor: '#faf9f7' }} aria-label="How our emotional support platform works">
            <div className="container mx-auto px-6 sm:px-8">
              <div className="text-center mb-16 sm:mb-20">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  How Our Free Support Platform Works
                </h1>
                <div className="w-24 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-8"></div>
                <p className="text-stone-600 max-w-3xl mx-auto text-lg leading-relaxed font-light">
                  Our platform provides a safe, judgment-free environment where you can express your feelings, receive emotional support, and connect with others who care about your wellbeing.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
                <article className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-amber-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                  <div className="relative bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-lg border border-stone-200/50 transition-all duration-500 transform group-hover:-translate-y-2">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-8 mx-auto" style={{ backgroundColor: '#d4b5a0' }}>
                      <MessageCircle size={28} className="text-stone-700" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-light mb-4 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Anonymous Chat Support
                    </h2>
                    <div className="w-12 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-6"></div>
                    <p className="text-stone-600 leading-relaxed text-center font-light">
                      Share your thoughts and feelings in complete anonymity with trained peer supporters who understand what you're going through. No registration required - just click and start talking.
                    </p>
                  </div>
                </article>
                
                <article className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-amber-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                  <div className="relative bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-lg border border-stone-200/50 transition-all duration-500 transform group-hover:-translate-y-2">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-8 mx-auto" style={{ backgroundColor: '#d4b5a0' }}>
                      <Ear size={28} className="text-stone-700" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-light mb-4 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Compassionate Listening
                    </h2>
                    <div className="w-12 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-6"></div>
                    <p className="text-stone-600 leading-relaxed text-center font-light">
                      Our compassionate listeners provide emotional support without judgment, helping you process difficult emotions and find clarity during tough times. Everyone deserves to be heard.
                    </p>
                  </div>
                </article>
                
                <article className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-amber-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105"></div>
                  <div className="relative bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-lg shadow-lg border border-stone-200/50 transition-all duration-500 transform group-hover:-translate-y-2">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-8 mx-auto" style={{ backgroundColor: '#d4b5a0' }}>
                      <Users size={28} className="text-stone-700" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-light mb-4 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Peer Support Community
                    </h2>
                    <div className="w-12 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-6"></div>
                    <p className="text-stone-600 leading-relaxed text-center font-light">
                      Connect with others who have similar experiences, creating meaningful connections and finding strength in shared understanding. You're part of a caring community.
                    </p>
                  </div>
                </article>
              </div>
              
              <div className="mt-16 sm:mt-20">
                <div className="bg-gradient-to-br from-white to-stone-50 p-10 sm:p-12 rounded-lg shadow-xl border border-stone-200/50 mx-auto max-w-4xl">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 text-center text-stone-800 tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Why Choose Our Free Support Platform
                  </h2>
                  <div className="w-24 h-0.5 bg-gradient-to-r from-stone-400 to-amber-300 mx-auto mb-10"></div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center group">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110" style={{ backgroundColor: '#e6d7c9' }}>
                        <Clock size={24} className="text-stone-700" />
                      </div>
                      <h3 className="font-medium mb-3 text-stone-800 tracking-wide">Free Support</h3>
                      <p className="text-stone-600 text-sm leading-relaxed font-light">
                        Emotional support is available whenever you need it, day or night, because mental health crises don't follow schedules. Always free.
                      </p>
                    </div>
                    
                    <div className="text-center group">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110" style={{ backgroundColor: '#e6d7c9' }}>
                        <Shield size={24} className="text-stone-700" />
                      </div>
                      <h3 className="font-medium mb-3 text-stone-800 tracking-wide">Complete Privacy</h3>
                      <p className="text-stone-600 text-sm leading-relaxed font-light">
                        Your conversations are completely anonymous and confidential, ensuring a safe space to express yourself freely without fear of judgment.
                      </p>
                    </div>
                    
                    <div className="text-center group">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110" style={{ backgroundColor: '#e6d7c9' }}>
                        <Heart size={24} className="text-stone-700" />
                      </div>
                      <h3 className="font-medium mb-3 text-stone-800 tracking-wide">Compassionate Care</h3>
                      <p className="text-stone-600 text-sm leading-relaxed font-light">
                        Our trained volunteers provide empathetic, non-judgmental support to help you through difficult times. You matter and your feelings are valid.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="text-center mt-12">
                  <Link
                    to="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3"
                    className="group inline-flex items-center px-10 py-4 text-base font-medium tracking-wider uppercase transition-all duration-500 ease-out transform hover:scale-105 text-white"
                    style={{ backgroundColor: '#8b7355' }}
                    aria-label="Start free anonymous emotional support chat now"
                  >
                    <span className="relative z-10">Start Free Chat Now</span>
                    <MessageCircle size={16} className="ml-3 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
       
        {/* Call to Action Section with Crisis Keywords */}
        <section className="relative overflow-hidden py-20 sm:py-24" aria-label="Get help now - you're not alone">
          <div className="absolute inset-0 bg-gradient-to-br from-stone-800 via-stone-700 to-amber-800"></div>
          <div className="container mx-auto px-6 sm:px-8 relative z-10">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-white tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                You're Not Alone in This
              </h2>
              <div className="w-24 h-0.5 bg-gradient-to-r from-amber-300 to-stone-400 mx-auto mb-8"></div>
              <p className="text-stone-200 max-w-2xl mx-auto text-lg sm:text-xl leading-relaxed mb-10 font-light">
                Take the first step towards healing. Whether you're dealing with depression, anxiety, crisis, or just need someone to talk to during tough times, our compassionate community is here to listen and support you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3"
                  className="group inline-flex items-center px-10 py-4 text-base font-medium tracking-wider uppercase transition-all duration-500 ease-out transform hover:scale-105 text-stone-800"
                  style={{ backgroundColor: '#d4b5a0' }}
                  aria-label="Start your healing journey with free emotional support chat"
                >
                  <span className="relative z-10">Start Your Healing Journey</span>
                  <Heart size={16} className="ml-3 transform group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
              <p className="text-stone-300 mt-6 text-sm">
                Free • Anonymous • Confidential
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HomePage;